<template>
  <button
    :class="classList"
    :type="type"
    :disabled="disabled"
  >
    <ui-loader
      v-if="loading"
      fill="#fff"
    />
    <div class="ui-button-content">
      <ui-badge
        v-if="count"
        class="ui-button-count"
        :text="count"
      />

      <f-sprite-icon
        v-if="icon && !count"
        :icon-link="icon"
      />
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="ui-button__text"
        v-html="sanitize(text)"
      />
      <!-- eslint-enable vue/no-v-html -->

      <slot />
    </div>
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { sanitize } from '@/utils/sanitize';

export default defineComponent({
  name: 'UiButton',

  props: {
    text: {
      type: String,
      default: '',
    },

    count: {
      type: [Number, String],
      default: 0,
    },

    type: {
      type: String,
      default: null,
    },

    icon: {
      type: String,
      default: '',
    },

    secondary: {
      type: Boolean,
      default: () => false,
    },

    blue: {
      type: Boolean,
      default: () => false,
    },

    flat: {
      type: Boolean,
      default: () => false,
    },

    wide: {
      type: Boolean,
      default: () => false,
    },

    small: {
      type: Boolean,
      default: () => false,
    },

    large: {
      type: Boolean,
      default: () => false,
    },

    loading: {
      type: Boolean,
      default: () => false,
    },

    disabled: {
      type: Boolean,
      default: () => false,
    },

  },

  setup() {
    return {
      sanitize,
    };
  },

  computed: {
    classList() {
      return [
        'ui-button',
        { 'ui-button-secondary': this.secondary },
        { 'ui-button-blue': this.blue },
        { 'ui-button-flat': this.flat },
        { 'ui-button-wide': this.wide },
        { 'ui-button-small': this.small },
        { 'ui-button-large': this.large },
        { 'ui-button-loading': this.loading },
        { 'ui-button-disabled': this.disabled },
      ];
    },
  },
});
</script>

<style lang="scss">
.ui-button {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 8px 16px;
  font-size: 14px;
  line-height: 24px;
  font-weight: 600;
  border-radius: 4px;
  transition: .2s ease all;
  cursor: pointer;

  .sprite-icon {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }

  &-content {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all .15s;
  }

  &-count {
    margin-right: 6px;
  }

  &-large {
    .sprite-icon {
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }
  }

  &-loading {
    pointer-events: none;
    .ui-button-content {
      transform: scale(.9);
      opacity: 0;
    }
    .ui-loader {
      position: absolute;
    }
  }

  /**
  COLORS
  */
  // PRIMARY
  background-color: $blue;
  color: #fff;

  // BLUE
  &-blue {
    color: $blue;
    background-color: rgba(26, 120, 187, .08);
  }

  &-disabled {
    pointer-events: none;
    background-color: $gray_lu;
  }

  // SECONDARY
  &-secondary {
    box-shadow: inset 0 0 0 1px $gray_lu;
    background-color: $white;
    color: $black;

    &.ui-button-disabled {
      color: $gray_lu;
    }
  }

  // FLAT
  &-flat {
    background-color: $white;
    color: $blue;

    &.ui-button-disabled {
      color: $gray_lu;
    }
  }

  /**
  SIZES
  */
  // SMALL
  &-small {
    padding: 4px 16px;
  }

  &-wide {
    width: 100%;
  }
}

@include breakpoint("tablet") {
  .ui-button {
    &-count {
      margin-right: 8px;
    }
  }
}

@include breakpoint("desktop") {
  .ui-button {
    &-large {
      font-size: 16px;
      padding: 16px 20px;
    }

    &:hover {
      background-color: $blue_hover;
    }
    &-secondary:hover {
      background-color: $gray_bgr;
    }
    &-flat:hover {
      color: $blue_hover;
      background-color: $blue_8;
    }
  }
}
</style>
